import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { AlertCircle, Trash } from 'tabler-icons-react';
import { toast } from 'react-toastify';
import { useForm } from '@mantine/form';

import api, { apiService } from 'shared/services/api';
import {
  Alert,
  Button,
  Checkbox,
  Divider,
  Group,
  LoadingOverlay,
  Modal,
  MultiSelect,
  NumberInput,
  Overlay,
  Radio,
  Select,
  TextInput,
} from '@mantine/core';
import { useTranslations } from 'shared/translations/useTranslations';
import useFetch from 'shared/hooks/useFetch';
import { DeviceTypeObservationType } from 'shared/types/deviceType';
import { SUPER_ADMIN, USER, USER_CONTROLLER } from 'shared/constants/user';

import LimitForm from '../LimitForm';
import { useDuplicateWarnings } from '../hooks';

interface Props {
  onClose: () => any;
  placeId: string | number;
  refreshParent: () => any;
  placeUuid: string;
  deviceTypes: {
    id: string;
    name: string;
    deviceTypeObjects: {
      id: string;
      name: string;
    }[];
    deviceTypeObservations: {
      id: string; // uuid
      name: string;
      type: string; // BAIT_COLLECT | REFILL
    }[];
  }[];
  open: boolean;
  devices: any[];
  zones: any[];
  currentLimits: any[];
}

interface DataLimits {
  limits: any[];
}

interface Data {
  deviceTypeObjects: any[];
  deviceTypeObservations: any[];
}

const CreateLimitModal = (props: Props) => {
  const { onClose, open, deviceTypes, devices, zones, placeUuid, refreshParent, currentLimits } =
    props;
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(true);
  const translations = useTranslations();

  const form = useForm({
    initialValues: {
      name: 'Limit #' + new Date().getTime().toString(),
      type: 'DEVICES', // DEVICE_TYPE
      devices: [],
      deviceType: [],
      zone: [],
      limits: [
        // {
        //   objectId: '',
        //   observationId: '',
        //   value: 0,
        //   type: 'NORMAL',
        //   limitType: 'OBJECT', // OBJECT | OBSERVATION
        //   percentage: 0, // for observations
        // },
      ],
      dangerousSum: 0,
      normalSum: 0,
    },
    // schema: zodResolver(schema),
  });

  // Function to check for duplicate limits
  const duplicateWarnings = useDuplicateWarnings({
    form,
    currentLimits,
    devices,
    zones,
    deviceTypes,
  });

  const onSubmit = async (values: any) => {
    try {
      setIsSaving(true);
      const response = await apiService({}).post(`/api/places/${placeUuid}/limits`, {
        name: values.name,
        type: values.type,
        limits: values.limits.map((limit: any) => ({
          objectId: limit.objectId,
          observationId: limit.observationId,
          value: limit.value,
          type: limit.type,
          limitType: limit.limitType,
          percentage: limit.percentage,
        })),
        dangerousSum: values.dangerousSum,
        normalSum: values.normalSum,
        deviceType: values.deviceType,
        zone: values.zone,
        devices: values.devices,
      });
      setIsSaving(false);
      setError(false);
      onClose();
      refreshParent();
      form.reset();
    } catch (error) {
      setIsSaving(false);
      setError(true);
    }
  };

  const possibleObjects = useMemo(() => {
    if (form.values.type === 'DEVICES') {
      const deviceUuids = form.values.devices;
      const filteredDevices = devices.filter(device => deviceUuids.includes(device.uuid)) || [];
      const deviceTypesIds = filteredDevices.flatMap(device => device.deviceTypeId) || [];
      const filteredDeviceTypes =
        deviceTypes.filter(deviceType => deviceTypesIds.includes(deviceType.id)) || [];
      const objects =
        filteredDeviceTypes
          .flatMap(deviceType => deviceType.deviceTypeObjects)
          .filter((object, index, self) => self.findIndex(t => t.id === object.id) === index) || [];
      return (
        objects?.map(object => ({
          label: object.name,
          value: object.id,
          disabled: form.values.limits?.find(l => l.objectId === object.id) ? true : false,
        })) || []
      );
    }
    if (['DEVICE_TYPE', 'DEVICE_TYPE_ALL_DEVICES'].includes(form.values.type)) {
      const deviceTypeId = form.values.deviceType;
      const filteredDeviceTypes =
        deviceTypes.filter(deviceType => deviceTypeId.includes(deviceType.id)) || [];
      const objects =
        filteredDeviceTypes
          .flatMap(deviceType => deviceType.deviceTypeObjects)
          .filter((object, index, self) => self.findIndex(t => t.id === object.id) === index) || [];
      return (
        objects.map(object => ({
          label: object.name,
          value: object.id,
          disabled: form.values.limits?.find(l => l.objectId === object.id) ? true : false,
        })) || []
      );
    }

    return [];
  }, [form.values]);


  const possibleObservations = useMemo(() => {
    if (['DEVICE_TYPE', 'DEVICE_TYPE_ALL_DEVICES'].includes(form.values.type)) {
      const deviceTypeId = form.values.deviceType;
      const filteredDeviceTypes =
        deviceTypes.filter(deviceType => deviceTypeId.includes(deviceType.id)) || [];
      const observations =
        filteredDeviceTypes
          .flatMap(deviceType => deviceType.deviceTypeObservations || [])
          .filter((observation, index, self) => self.findIndex(t => t.id === observation.id) === index) || [];
      return observations.map(observation => ({
        label: observation.name,
        value: observation.id,
        disabled: observation.type !== 'BAIT_COLLECT' || form.values.limits?.find(l => l.objectId === observation.id && l.limitType === 'OBSERVATION') ? true : false,
      }));
    }
    if (form.values.type === 'DEVICES') {
      const deviceUuids = form.values.devices;
      const filteredDevices = devices.filter(device => deviceUuids.includes(device.uuid)) || [];
      const deviceTypesIds = filteredDevices.flatMap(device => device.deviceTypeId) || [];
      const filteredDeviceTypes =
        deviceTypes.filter(deviceType => deviceTypesIds.includes(deviceType.id)) || [];
      const observations =
        filteredDeviceTypes
          .flatMap(deviceType => deviceType.deviceTypeObservations || [])
          .filter((observation, index, self) => self.findIndex(t => t.id === observation.id) === index) || [];
      return observations.map(observation => ({
        label: observation.name,
        value: observation.id,
        disabled: observation.type !== 'BAIT_COLLECT' || form.values.limits?.find(l => l.objectId === observation.id && l.limitType === 'OBSERVATION') ? true : false,
      }));
    }
    return [];
  }, [form.values, deviceTypes, devices]);

  const selectedDevices = useMemo(() => {
    return devices.filter(device => form.values.devices.includes(device.uuid)) || [];
  }, [form.values.devices]);

  const isProperlySelectedAnything = useMemo(() => {
    return (
      form.values.devices.length > 0 ||
      form.values.deviceType.length > 0 ||
      form.values.zone.length > 0
    );
  }, [form.values]);
  return (
    <Modal opened={open} onClose={onClose} title={translations.limits.title} size="lg">
      {/* {readOnly && <Alert color="red">{translations.global.onlyAdminCanEdit}</Alert>} */}

      {/* Duplicate warnings */}
      {duplicateWarnings.length > 0 && (
        <Alert
          icon={<AlertCircle size={16} />}
          title="Ostrzeżenie o duplikatach"
          color="yellow"
          mb={15}>
          <div>
            Wykryto potencjalne duplikaty limitów:
            <ul>
              {duplicateWarnings.map((warning, index) => (
                <li key={index}>{warning}</li>
              ))}
            </ul>
            Utworzenie tego limitu może powodować konflikty z istniejącymi limitami.
          </div>
        </Alert>
      )}

      <form onSubmit={form.onSubmit(onSubmit)}>
        <TextInput label="Nazwa limitu" {...form.getInputProps('name')} mb={15} />
        <Radio.Group
          name="type"
          {...form.getInputProps('type', { type: 'checkbox' })}
          mb={20}
          defaultValue="DEVICES">
          <Radio label="Limit dla indywidualnych urządzeń" value="DEVICES" mb={10} />
          <Radio label="Limit dla typu urządzenia i strefy" value="DEVICE_TYPE" mb={10} />
          <Radio
            label="Limit dla typu urządzenia i strefy dla indywidualnych urządzeń"
            value="DEVICE_TYPE_ALL_DEVICES"
            mb={10}
          />
        </Radio.Group>

        {form.values.type === 'DEVICES' && (
          <MultiSelect
            multiple
            label="Urządzenia"
            searchable
            clearable
            data={devices.map(device => ({
              label: device.uniqueId,
              value: device.uuid,
            }))}
            {...form.getInputProps('devices')}
          />
        )}
        {['DEVICE_TYPE', 'DEVICE_TYPE_ALL_DEVICES'].includes(form.values.type) && (
          <Group>
            <MultiSelect
              multiple
              label="Typ urządzenia"
              searchable
              clearable
              data={deviceTypes.map(deviceType => ({
                label: deviceType.name,
                value: deviceType.id,
              }))}
              {...form.getInputProps('deviceType')}
            />
            <MultiSelect
              multiple
              label="Strefa"
              searchable
              clearable
              data={zones.map(zone => ({
                label: zone.name,
                value: zone.id,
              }))}
              {...form.getInputProps('zone')}
            />
          </Group>
        )}
        {isProperlySelectedAnything && (
          <>
            {['DEVICES', 'DEVICE_TYPE', 'DEVICE_TYPE_ALL_DEVICES'].includes(form.values.type) && (
              <>
                <table style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <td style={{ fontSize: 12 }}>Nazwa</td>
                      <td style={{ fontSize: 12 }}>Wartość</td>
                      <td style={{ fontSize: 12 }}>Typ</td>
                    </tr>
                  </thead>
                  <tbody>
                    {form.values.limits?.filter(l => l.limitType === 'OBJECT').map((limit, index) => {
                      const actualIndex = form.values.limits.findIndex(l => l === limit);
                      return (
                        <tr key={actualIndex}>
                          <td width="30%" style={{ paddingRight: '20px' }}>
                            <Select
                              searchable
                              data={possibleObjects}
                              {...form.getInputProps(`limits.${actualIndex}.objectId`)}
                            />
                          </td>
                          <td>
                            <NumberInput {...form.getInputProps(`limits.${actualIndex}.value`)} />
                          </td>
                          <td>
                            <Select
                              data={[
                                {
                                  label: translations.objects.normal,
                                  value: 'NORMAL',
                                },
                                { label: translations.objects.dangerous, value: 'DANGEROUS' },
                              ]}
                              {...form.getInputProps(`limits.${actualIndex}.type`)}
                            />
                          </td>
                          <td>
                            <Button
                              variant="outline"
                              onClick={() => form.removeListItem('limits', actualIndex)}
                              color="red"
                              size="xs"
                              // disabled={form.values.limits.length === 1}
                              >
                              <Trash size={16} />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </>
            )}
            
            {/* Observations table */}
            {['DEVICES', 'DEVICE_TYPE', 'DEVICE_TYPE_ALL_DEVICES'].includes(form.values.type) && possibleObservations.length > 0 && (
              <>
                <Divider my={15} />
                <h5>Limity dla obserwacji</h5>
                <table style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <td style={{ fontSize: 12 }}>Nazwa obserwacji</td>
                      <td style={{ fontSize: 12 }}>Procent (%)</td>
                    </tr>
                  </thead>
                  <tbody>
                    {form.values.limits?.filter(l => l.limitType === 'OBSERVATION').map((limit, index) => {
                      const actualIndex = form.values.limits.findIndex(l => l === limit);
                      return (
                        <tr key={actualIndex}>
                          <td width="30%" style={{ paddingRight: '20px' }}>
                            <Select
                              searchable
                              data={possibleObservations}
                              {...form.getInputProps(`limits.${actualIndex}.observationId`)}
                            />
                          </td>
                          <td>
                            <NumberInput 
                              min={1}
                              max={99}
                              placeholder="1-99"
                              {...form.getInputProps(`limits.${actualIndex}.percentage`)} 
                            />
                          </td>
                          <td>
                            <Button
                              variant="outline"
                              onClick={() => form.removeListItem('limits', actualIndex)}
                              color="red"
                              size="xs">
                              <Trash size={16} />
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </>
            )}
            
            <Button
              mt={15}
              variant="outline"
              disabled={
                !!form.values.limits?.find(
                  l => l.objectId === undefined || l.type === undefined || (l.limitType === 'OBJECT' ? l.value === undefined : l.percentage === undefined),
                )
              }
              onClick={() => {
                form.insertListItem('limits', {
                  objectId: '',
                  observationId: '',
                  type: 'DANGEROUS',
                  value: 0,
                  limitType: 'OBJECT',
                  percentage: 0,
                });
              }}>
              Dodaj limit dla szkodnika
            </Button>
            
            {possibleObservations.length > 0 && (
              <Button
                mt={15}
                ml={15}
                variant="outline"
                disabled={
                  !!form.values.limits?.find(
                    l => l.objectId === undefined || l.type === undefined || l.percentage === undefined,
                  )
                }
                onClick={() => {
                  form.insertListItem('limits', {
                    objectId: '',
                    observationId: '',
                    type: 'NORMAL',
                    value: 0,
                    limitType: 'OBSERVATION',
                    percentage: 0,
                  });
                }}>
                Dodaj limit dla obserwacji
              </Button>
            )}
            {/* {form.values.type === 'SUM' && ( */}
            <>
              <Divider my={15} />
              Aby zastosować limit dla sumy szkodników
              <NumberInput
                label="Suma szkodników niebezpiecznych"
                description="Jezeli wartosc to 0, to limit nie jest zaaplikowany"
                {...form.getInputProps('dangerousSum')}
              />
              <Divider my={15} />
              <NumberInput
                label="Suma szkodników normalnych"
                description="Jezeli wartosc to 0, to limit nie jest zaaplikowany"
                {...form.getInputProps('normalSum')}
              />
            </>
            {/* )} */}
            <Divider my={15} />
          </>
        )}
        <Alert
          mt={15}
          icon={<AlertCircle size={16} />}
          title={translations.global.information}
          color="blue">
          <div>
            {form.values.type === 'DEVICES' && (
              <>
                {/* TODO: add information based on type and selected values */}
                Limit zostanie zastosowany dla urządzeń:
                <ul>
                  {selectedDevices?.map(device => (
                    <li key={device.uuid}>{device.uniqueId}</li>
                  ))}
                </ul>
                Przekroczony limit w każdym urządzeniu zostanie wyświetlony jeśli:
                <ul>
                  {/* For selected objects, show information that if object is dangerous, then limit will be applied */}
                  {form.values.limits?.map(limit =>
                    limit.value > 0 ? (
                      <li key={limit.objectId}>
                        {limit.objectId &&
                          possibleObjects.find(object => object.value === limit.objectId)
                            ?.label && (
                            <div>
                              Jeśli będzie przekroczona wartość <strong>{limit.value}</strong> dla{' '}
                              <strong>
                                {
                                  possibleObjects.find(object => object.value === limit.objectId)
                                    ?.label
                                }
                              </strong>{' '}
                            </div>
                          )}
                      </li>
                    ) : null,
                  )}
                </ul>
                {form.values.dangerousSum > 0 && (
                  <>
                    Limit dla sumy szkodnikow niebezpiecznych zostanie pokazany jeśli:
                    <ul>
                      {/* For selected objects, show information that if object is dangerous, then limit will be applied */}

                      <li>
                        Wartość dla{' '}
                        {form.values.limits
                          ?.map(
                            limit =>
                              possibleObjects.find(object => object.value === limit.objectId)
                                ?.label,
                          )
                          .join(' + ')}{' '}
                        będzie większa lub równa <strong>{form.values.dangerousSum}</strong>
                      </li>
                    </ul>
                  </>
                )}
                {form.values.normalSum > 0 && (
                  <>
                    Limit dla sumy szkodnikow normalnych zostanie pokazany jeśli:
                    <ul>
                      {/* For selected objects, show information that if object is dangerous, then limit will be applied */}

                      <li>
                        Wartość dla{' '}
                        {form.values.limits
                          ?.map(
                            limit =>
                              possibleObjects.find(object => object.value === limit.objectId)
                                ?.label,
                          )
                          .join(' + ')}{' '}
                        będzie większa lub równa <strong>{form.values.normalSum}</strong>
                      </li>
                    </ul>
                  </>
                )}
              </>
            )}
            {form.values.type === 'DEVICE_TYPE' && (
              <>
                Limit zostanie zastosowany dla typu urządzenia:
                {form.values.deviceType.length > 0 && (
                  <ul>
                    {form.values.deviceType?.map(deviceTypeId => (
                      <li key={deviceTypeId}>
                        {deviceTypes.find(deviceType => deviceType.id === deviceTypeId)?.name}
                        {form.values.zone.length > 0 ? (
                          <> w strefach </>
                        ) : (
                          <strong> we wszystkich strefach </strong>
                        )}
                        <strong>
                          {zones
                            .filter(zone => form.values.zone.includes(zone.id))
                            .map(zone => zone.name)
                            .join(', ')}
                        </strong>
                      </li>
                    ))}
                  </ul>
                )}
                Limit zostanie wyświetlony jeśli:
                <ul>
                  {form.values.deviceType?.map(deviceTypeId => (
                    <li key={deviceTypeId}>
                      {deviceTypes.find(deviceType => deviceType.id === deviceTypeId)?.name}{' '}
                      {form.values.zone.length > 0 ? (
                        <> w strefach </>
                      ) : (
                        <strong> we wszystkich strefach </strong>
                      )}
                      <strong>
                        {' '}
                        {zones
                          .filter(zone => form.values.zone.includes(zone.id))
                          .map(zone => zone.name)
                          .join(', ')}
                      </strong>
                      <ul>
                        {form.values.limits?.map(limit =>
                          limit.value > 0 ? (
                            <li key={limit.objectId}>
                              {limit.objectId &&
                                possibleObjects.find(object => object.value === limit.objectId)
                                  ?.label && (
                                  <div>
                                    <strong>
                                      {
                                        possibleObjects.find(
                                          object => object.value === limit.objectId,
                                        )?.label
                                      }
                                    </strong>{' '}
                                    {'>='} <strong>{limit.value}</strong>{' '}
                                  </div>
                                )}
                            </li>
                          ) : null,
                        )}
                      </ul>
                    </li>
                  ))}
                </ul>
                {form.values.dangerousSum > 0 && (
                  <>
                    Limit dla sumy szkodnikow niebezpiecznych zostanie pokazany jeśli:
                    <ul>
                      {form.values.deviceType?.map(deviceTypeId => (
                        <li key={deviceTypeId}>
                          {deviceTypes.find(deviceType => deviceType.id === deviceTypeId)?.name} -
                          Suma dla szkodników niebezpiecznych będzie większa lub równa{' '}
                          <strong>{form.values.dangerousSum}</strong>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
                {form.values.normalSum > 0 && (
                  <>
                    Limit dla sumy szkodnikow normalnych zostanie pokazany jeśli:
                    <ul>
                      {form.values.deviceType?.map(deviceTypeId => (
                        <li key={deviceTypeId}>
                          {deviceTypes.find(deviceType => deviceType.id === deviceTypeId)?.name} -
                          Suma dla szkodników normalnych będzie większa lub równa{' '}
                          <strong>{form.values.normalSum}</strong>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </>
            )}
            {form.values.type === 'DEVICE_TYPE_ALL_DEVICES' && (
              <>
                Limit zostanie zastosowany dla typu urządzenia:
                {form.values.deviceType.length > 0 && (
                  <ul>
                    {form.values.deviceType?.map(deviceTypeId => (
                      <li key={deviceTypeId}>
                        {deviceTypes.find(deviceType => deviceType.id === deviceTypeId)?.name}
                        {form.values.zone.length > 0 ? (
                          <> w strefach </>
                        ) : (
                          <strong> we wszystkich strefach </strong>
                        )}
                        <strong>
                          {zones
                            .filter(zone => form.values.zone.includes(zone.id))
                            .map(zone => zone.name)
                            .join(', ')}
                        </strong>
                      </li>
                    ))}
                  </ul>
                )}
                Limit zostanie wyświetlony dla poszczególnego urządzenia jeśli:
                <ul>
                  {form.values.deviceType?.map(deviceTypeId => (
                    <li key={deviceTypeId}>
                      {deviceTypes.find(deviceType => deviceType.id === deviceTypeId)?.name}{' '}
                      {form.values.zone.length > 0 ? (
                        <> w strefach </>
                      ) : (
                        <strong> we wszystkich strefach </strong>
                      )}
                      <strong>
                        {' '}
                        {zones
                          .filter(zone => form.values.zone.includes(zone.id))
                          .map(zone => zone.name)
                          .join(', ')}
                      </strong>
                      <ul>
                        {form.values.limits?.map(limit =>
                          limit.value > 0 ? (
                            <li key={limit.objectId}>
                              {limit.objectId &&
                                possibleObjects.find(object => object.value === limit.objectId)
                                  ?.label && (
                                  <div>
                                    <strong>
                                      {
                                        possibleObjects.find(
                                          object => object.value === limit.objectId,
                                        )?.label
                                      }
                                    </strong>{' '}
                                    {'>='} <strong>{limit.value}</strong>{' '}
                                  </div>
                                )}
                            </li>
                          ) : null,
                        )}
                      </ul>
                    </li>
                  ))}
                </ul>
                {form.values.dangerousSum > 0 && (
                  <>
                    Limit dla sumy szkodnikow niebezpiecznych zostanie pokazany jeśli:
                    <ul>
                      {form.values.deviceType?.map(deviceTypeId => (
                        <li key={deviceTypeId}>
                          {deviceTypes.find(deviceType => deviceType.id === deviceTypeId)?.name} -
                          Suma dla szkodników niebezpiecznych dla poszczególnego urządzenia będzie
                          większa lub równa <strong>{form.values.dangerousSum}</strong>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
                {form.values.normalSum > 0 && (
                  <>
                    Limit dla sumy szkodnikow normalnych zostanie pokazany jeśli:
                    <ul>
                      {form.values.deviceType?.map(deviceTypeId => (
                        <li key={deviceTypeId}>
                          {deviceTypes.find(deviceType => deviceType.id === deviceTypeId)?.name} -
                          Suma dla szkodników normalnych dla poszczególnego urządzenia będzie
                          większa lub równa <strong>{form.values.normalSum}</strong>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </>
            )}
          </div>
        </Alert>

        {/* {isLoadedCombined && !readOnly && (
          <LimitForm form={form} data={deviceTypeData} readOnly={readOnly} />
        )} */}
        {/* {isLoadedCombined && !readOnly && (
          <Alert
            icon={<AlertCircle size={16} />}
            title={translations.global.information}
            color="blue">
            {translations.limits.informationDetails}
          </Alert>
        )} */}
        <Group position="right" mt={20}>
          <Button color="primary" loading={isSaving} type="submit" disabled={isSaving || !isLoaded}>
            {translations.global.save}
          </Button>
          <Button variant="outline" onClick={onClose}>
            {translations.global.cancel}
          </Button>
        </Group>
      </form>
    </Modal>
  );
};

export default CreateLimitModal;
